<template>
  <div class="full-h col user-select-disabled mx-1 h-full">
    <Transition name="fade" mode="out-in">
      <EnterEmail
        v-if="currentScreen === 'ENTER_EMAIL'"
        :error-state="errorState"
        @sendOTPResponse="onSendOTPResponse"
      />

      <SignUp
        v-else-if="currentScreen === 'SIGN_UP'"
        :email="userEmail"
        :user-flow="userFlow"
        :back-button="backButton"
        @signUp="onSendOTPResponse"
        @back="onBack"
      />

      <OneTimePassword
        v-else-if="currentScreen === 'ONE_TIME_PASSWORD'"
        :email="userEmail"
        :back-button="backButton"
        @back="onBack"
        @loginSuccess="onLoginSuccess"
      />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import EnterEmail from "./EnterEmail.vue";
import SignUp from "./SignUp.vue";
import OneTimePassword from "./OneTimePassword.vue";

import { Ref, ref } from "vue";
import { useRefHistory } from "@vueuse/core";
import { sendOTP, SendOTPResponseMessage } from "../services/api";
import rules from "../common/rules";
import { validateRules } from "@/utils/rules/rules";
import { logger } from "@/services/logger";
import type { UserFlow } from "@/features/auth/services/api";
type AuthScreen = "ENTER_EMAIL" | "SIGN_UP" | "ONE_TIME_PASSWORD";

interface IProps {
  email?: string;
  userFlow?: UserFlow;
  backButton?: boolean;
  title?: string;
  description?: string;
}

const props = withDefaults(defineProps<IProps>(), {
  backButton: true,
});

const emit = defineEmits<{
  (event: "login-success"): void;
}>();

const userEmail: Ref<string> = ref("");
const errorState = ref(false);

const currentScreen: Ref<AuthScreen> = ref("ENTER_EMAIL");
const currentScreenHistory = useRefHistory(currentScreen);

await tryInitWithEmail();

async function tryInitWithEmail() {
  try {
    if (!props.email) return;
    const emailParamValid = (await validateRules(rules.email, props.email)) === true;
    if (emailParamValid) {
      try {
        const sendOTPResponse = await sendOTP(props.email);
        if (sendOTPResponse) {
          onSendOTPResponse(sendOTPResponse, props.email);
        }
      } catch (err) {
        errorState.value = true;
      }
    }
  } catch (e) {
    logger.error("AuthFlow - Error when trying to init with email");
  }
}

async function onSendOTPResponse(sendOTPResponse: SendOTPResponseMessage, email: string) {
  userEmail.value = email;
  if (sendOTPResponse === "USER_DOESNT_EXIST") {
    changeScreen("SIGN_UP");
  } else if (sendOTPResponse === "CODE_SENT") {
    changeScreen("ONE_TIME_PASSWORD");
  }
}

async function changeScreen(screen: AuthScreen) {
  currentScreen.value = screen;
}

function onBack() {
  currentScreenHistory.undo();
}

function onLoginSuccess() {
  emit("login-success");
  window.parent.postMessage("login-success", "*");
}
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
